import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { useAutoSignIn } from '../../../../shared/hooks/accountHook';
import {
  useShareToPatientLink,
  useSignUpForShareToPatient,
} from '../../../../shared/hooks/linkHook';
import {
  useSignInRedirect,
  useSignOut,
} from '../../../../shared/hooks/oauthHook';
import { useShareToPatientSave } from '../../../../shared/hooks/shareToPatientHook';
import { useSignInRedirectWithNewWindow } from '../../hooks/oauthHook';
import { LayoutHeaderContextProvider } from '../Layout/LayoutHeader';
import { PreviewLayout } from '../Layout/PreviewLayout';
import { PreviewImageList } from '../Preview/PreviewImageList';
import { SessionIdContextProvider } from '../Preview/SessionIdContext';

import { ShareToPatientCaseHookProvider } from './ShareToPatientCaseHookProvider';
import { ShareToPatientVerification } from './ShareToPatientVerification';

export const ShareToPatientPreview = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [sessionId, setSessionId] = useState('');
  const [sessionEndAt, setSessionEndAt] = useState('');
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'features.shareToPatientImageList',
  });

  const { data: link } = useShareToPatientLink(id);
  const linkEndAt = link?.endAt ?? '';

  const signInRedirect = useSignInRedirect();
  useEffect(() => {
    if (linkEndAt) {
      if (isOverAMonth(linkEndAt)) {
        signInRedirect({ register: true });
      }
    }
  }, [linkEndAt, signInRedirect]);

  const isOverAMonth = (linkDate: string) => {
    const differenceInMilliseconds = Date.now() - +new Date(linkDate);
    const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
    return differenceInDays > 30;
  };

  const redirectUrlToAutoClose = useMemo(() => {
    const searchParams = new URLSearchParams({
      redirectUrl: location.pathname,
    });
    return `/auto-close?${searchParams}`;
  }, []);
  const openSignInWindow = () =>
    signInRedirect(
      { redirect: redirectUrlToAutoClose },
      true,
    ) as Promise<Window>;
  const openSignUpWindow = async () => {
    await signout();
    const { presetId } = await signup(id);
    return signInRedirect(
      {
        register: true,
        preset: presetId,
        redirect: redirectUrlToAutoClose,
      },
      true,
    ) as Promise<Window>;
  };

  const { mutateAsync: signout } = useSignOut();
  const { mutateAsync: signup } = useSignUpForShareToPatient({
    onError: (error, variables, context) => {
      // eslint-disable-next-line no-alert
      window.alert(`${t('linkExpired')}`);
      navigate(`/share-to-patient/${id}`);
      console.log('error, variables, context : ', error, variables, context);
    },
  });

  const signinRedirectWithNewWindow = useSignInRedirectWithNewWindow();
  const onClickLoginButton = () => {
    signinRedirectWithNewWindow({ openWindow: openSignInWindow });
  };
  const onClickSignUpButton = () => {
    signinRedirectWithNewWindow({ openWindow: openSignUpWindow });
  };
  const onClickLogoutButton = async () => {
    await signout();
  };

  const { data: user, refetch: requestUser } = useAutoSignIn();
  const { mutate: saveImages, isPending } = useShareToPatientSave({
    onSuccess() {
      window.location.replace('/images-list');
    },
  });
  const onSave = async () => {
    if (!user) {
      const onStartLogin = () => {
        setDisableSaveButton(true);
      };
      const onCancelLogin = () => {
        setDisableSaveButton(false);
      };
      const onSuccessLogin = async (tryCloseWindow: () => void) => {
        setDisableSaveButton(false);
        const { data: newUser } = await requestUser();
        if (newUser) {
          saveImages({ uri: id });
          tryCloseWindow();
        }
      };

      await signinRedirectWithNewWindow({
        openWindow: openSignInWindow,
        onStart: onStartLogin,
        onSuccess: onSuccessLogin,
        onCancel: onCancelLogin,
      });
    } else {
      saveImages({ uri: id });
    }
  };

  return (
    <>
      {!sessionId.length ? (
        <ShareToPatientVerification
          setSessionId={setSessionId}
          setSessionEndAt={setSessionEndAt}
        />
      ) : (
        <SessionIdContextProvider
          sessionId={sessionId}
          setSessionId={setSessionId}>
          <ShareToPatientCaseHookProvider>
            <LayoutHeaderContextProvider
              onClickLoginButton={onClickLoginButton}
              onClickSignUpButton={onClickSignUpButton}
              onClickLogoutButton={onClickLogoutButton}>
              <PreviewLayout>
                <PreviewImageList
                  sessionEndAt={sessionEndAt}
                  linkEndAt={linkEndAt}
                  onSave={onSave}
                  isSaving={isPending}
                  disableSaveButton={disableSaveButton}
                />
              </PreviewLayout>
            </LayoutHeaderContextProvider>
          </ShareToPatientCaseHookProvider>
        </SessionIdContextProvider>
      )}
    </>
  );
};
